
import { useEffect, useState } from 'react';
import { Loader, Message } from 'rsuite';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiCalendar, FiInbox, FiUserCheck } from 'react-icons/fi';
import AdminDashboard from '../../components/AdminDashboard';
import { DriverStatus, OrderStatus } from '../../my-enum';
import { Order } from '../../my-type';
import { authUserData } from '../../service';
import { getDriversByTeamCode } from '../../service/DriverService';
import { getOrdersByTeamCode } from '../../service/OrderService';

type DriverStats = {
  pendingApproval: number | null;
  total: number | null;
  disabled: number | null;
};

type OrderStats = {
  todayTotal: number | null;
  monthTotal: number | null;
  monthPending: number | null;
  nextMonthTotal: number | null;
  nextMonthPending: number | null;
};

const parseDate = (value: Date | string | null | undefined) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getTaipeiParts = (date: Date) => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  const parts = formatter.formatToParts(date);
  const year = Number(parts.find((part) => part.type === 'year')?.value ?? '0');
  const month = Number(parts.find((part) => part.type === 'month')?.value ?? '0');
  const day = Number(parts.find((part) => part.type === 'day')?.value ?? '0');
  return { year, month, day };
};

const isSameTaipeiDay = (value: Date | string | null | undefined, target: { year: number | null; month: number | null; day: number }) => {
  const date = parseDate(value);
  if (!date) return false;
  const parts = getTaipeiParts(date);
  return parts.year === target.year && parts.month === target.month && parts.day === target.day;
};

const formatYearMonth = (year: number, month: number) => {
  return `${year}-${String(month).padStart(2, '0')}`;
};

const AdminPage = () => {
  const [reloadKey, setReloadKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [driverStats, setDriverStats] = useState<DriverStats>({
    pendingApproval: null,
    total: null,
    disabled: null
  });
  const [orderStats, setOrderStats] = useState<OrderStats>({
    todayTotal: null,
    monthTotal: null,
    monthPending: null,
    nextMonthTotal: null,
    nextMonthPending: null
  });

  const authUser = authUserData();

  useEffect(() => {
    let active = true;
    setLoading(true);
    setErrorMessage(null);
    const loadDashboardStats = async () => {
      if (!authUser) {
        setErrorMessage('請先登入後再查看統計資訊。');
        setLoading(false);
        return;
      }

      const teamCode = authUser.team?.code;
      if (!teamCode) {
        setErrorMessage('找不到車隊資訊，請稍後再試。');
        setLoading(false);
        return;
      }

      const now = new Date();
      const nowParts = getTaipeiParts(now);
      const nextMonthParts = {
        year: nowParts.month === 12 ? nowParts.year + 1 : nowParts.year,
        month: nowParts.month === 12 ? 1 : nowParts.month + 1
      };
      const currentYearMonth = formatYearMonth(nowParts.year, nowParts.month);
      const nextYearMonth = formatYearMonth(nextMonthParts.year, nextMonthParts.month);

      const results = await Promise.allSettled([
        getDriversByTeamCode(teamCode),
        getOrdersByTeamCode(teamCode, currentYearMonth),
        getOrdersByTeamCode(teamCode, nextYearMonth)
      ]);
      if (!active) return;

      const drivers = results[0].status === 'fulfilled' ? results[0].value : null;
      const currentOrders = results[1].status === 'fulfilled' ? results[1].value : null;
      const nextOrders = results[2].status === 'fulfilled' ? results[2].value : null;
      const current = currentOrders?.filter(order => order.status !== OrderStatus.已取消);
      const next = nextOrders?.filter(order => order.status !== OrderStatus.已取消);
      // Use team-scoped monthly orders, not the driver's cross-team pickup pool.
      const pendingCount = (orders: Order[] | undefined) => orders
        ? orders.filter(order => order.status === OrderStatus.派車中 && order.paymentPaid).length
        : null;

      setDriverStats({
        pendingApproval: drivers?.filter(driver => driver.status === DriverStatus.未驗證).length ?? null,
        total: drivers?.length ?? null,
        disabled: drivers?.filter(driver => driver.status === DriverStatus.停用中).length ?? null
      });
      setOrderStats({
        todayTotal: current?.filter(order => isSameTaipeiDay(order.rideTime, nowParts)).length ?? null,
        monthTotal: current?.length ?? null,
        monthPending: pendingCount(current),
        nextMonthTotal: next?.length ?? null,
        nextMonthPending: pendingCount(next)
      });
      const unavailable = [
        !drivers && '司機資料',
        !currentOrders && '本月訂單',
        !nextOrders && '下月訂單'
      ].filter(Boolean);
      if (unavailable.length) {
        setErrorMessage(`${unavailable.join('、')}暫時無法載入，相關數字以「—」顯示。其餘統計仍可查看。`);
      }
      setLoading(false);
    };

    loadDashboardStats().catch(() => {
      if (!active) return;
      setErrorMessage('無法載入車隊統計，請稍後重試。');
      setLoading(false);
    });
    return () => { active = false; };
  }, [authUser, reloadKey]);

  return (
    <AdminDashboard title="團隊概要">
      <div className="fleet-toolbar">
        <span>{authUser?.team?.name ?? '車隊資訊'} · {new Intl.DateTimeFormat('zh-TW', { timeZone: 'Asia/Taipei', dateStyle: 'long' }).format(new Date())}</span>
        <Link className="fleet-button fleet-button--primary" to="/orders"><FiCalendar aria-hidden="true" />查看訂單月曆</Link>
      </div>
      {errorMessage && <Message type="warning" showIcon style={{ marginBottom: 16 }}>
        {errorMessage} <button className="fleet-button" disabled={loading} onClick={() => setReloadKey(key => key + 1)}>重新載入統計</button>
      </Message>}
      {loading ? (
        <div className="fleet-empty" role="status"><Loader content="正在載入車隊營運資訊…" /></div>
      ) : <>
        <div className="fleet-stats">
          <div className="fleet-stat"><span>今日行程</span><strong>{orderStats.todayTotal ?? '—'}</strong><small>筆訂單 · 不含已取消</small></div>
          <div className="fleet-stat"><span>本月訂單</span><strong>{orderStats.monthTotal ?? '—'}</strong><small>筆訂單 · 不含已取消</small></div>
          <div className="fleet-stat fleet-stat--attention"><span>本月待派</span><strong>{orderStats.monthPending ?? '—'}</strong><small>筆訂單 · 待安排司機</small></div>
          <div className="fleet-stat"><span>下月訂單</span><strong>{orderStats.nextMonthTotal ?? '—'}</strong><small>其中 {orderStats.nextMonthPending ?? '—'} 筆待派</small></div>
        </div>
        <div className="fleet-overview-grid">
          <section className="fleet-card"><h2>營運待辦</h2>
            <Link className="fleet-task-link" to="/orders"><FiCalendar aria-hidden="true" /><span>安排本月行程<small>檢視訂單日期與派車狀態</small></span><b>{orderStats.monthPending ?? '—'}</b><FiArrowRight aria-hidden="true" /></Link>
            <Link className="fleet-task-link" to="/drivers/approval"><FiUserCheck aria-hidden="true" /><span>審核加入車隊的司機<small>確認司機與車輛資料</small></span><b>{driverStats.pendingApproval ?? '—'}</b><FiArrowRight aria-hidden="true" /></Link>
            <Link className="fleet-task-link" to="/brand-orders"><FiInbox aria-hidden="true" /><span>查看品牌派單<small>核對服務要求與承接金額</small></span><FiArrowRight aria-hidden="true" /></Link>
          </section>
          <section className="fleet-card"><h2>車隊人員</h2><p>掌握司機資料與審核進度。</p>
            <dl className="fleet-mini-stats"><div><dt>司機總數</dt><dd>{driverStats.total ?? '—'}</dd></div><div><dt>待核可</dt><dd>{driverStats.pendingApproval ?? '—'}</dd></div><div><dt>已停用</dt><dd>{driverStats.disabled ?? '—'}</dd></div></dl>
            <Link className="fleet-button" to="/users">管理團隊司機 <FiArrowRight aria-hidden="true" /></Link>
          </section>
        </div>
      </>}
    </AdminDashboard>
  );
};

export default AdminPage;
